import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Edit, Plus, Trash2, DollarSign, XCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useToast } from '../components/ui/use-toast';
import AddOwnerModal from '../components/AddOwnerModal';
import AddBankAccountModal from '../components/AddBankAccountModal';
import UploadDocumentsModal from '../components/UploadDocumentsModal';
import EditOwnerModal from '../components/EditOwnerModal';
import EditBankAccountModal from '../components/EditBankAccountModal';
import EditClientModal from '../components/EditClientModal';
import OfferBuilderModal from '../components/OfferBuilderModal';
import ApprovalTermsModal from '../components/ApprovalTermsModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [client, setClient] = useState(null);
  const [deals, setDeals] = useState([]);
  const [owners, setOwners] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showAddOwner, setShowAddOwner] = useState(false);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  const [showUploadDocs, setShowUploadDocs] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showOfferBuilder, setShowOfferBuilder] = useState(false);
  const [showApprovalTerms, setShowApprovalTerms] = useState(false);

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      
      const clientRes = await axios.get(`${API_BASE}/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(clientRes.data);
      
      // Load related data
      const [dealsRes, ownersRes, bankRes] = await Promise.all([
        axios.get(`${API_BASE}/clients/${id}/deals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/clients/${id}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/clients/${id}/bank-accounts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDeals(dealsRes.data || []);
      setOwners(ownersRes.data || []);
      setBankAccounts(bankRes.data || []);
    } catch (error) {
      console.error('Error loading client:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/clients/${id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  useEffect(() => {
    loadClient();
    loadDocuments();
  }, [id]);

  const deleteOwner = async (ownerId) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/clients/${id}/owners/${ownerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Owner deleted successfully' });
      loadClient();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete owner', variant: 'destructive' });
    }
  };

  const deleteBankAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/clients/${id}/bank-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Bank account deleted successfully' });
      loadClient();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete account', variant: 'destructive' });
    }
  };

  const deleteDeal = async (dealId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Deal deleted successfully' });
      loadClient();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete deal', variant: 'destructive' });
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/clients/${id}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Document deleted successfully' });
      loadDocuments();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete document', variant: 'destructive' });
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Decline this client? Status will be updated to Declined.')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/clients/${id}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Client declined' });
      navigate('/clients');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to decline client', variant: 'destructive' });
    }
  };

  const handleApproveWithTerms = async (termsData) => {
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/clients/${id}/offers`, termsData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Client approved! Deal moved to Submissions.' });
      setShowApprovalTerms(false);
      navigate('/submitted-deals');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to approve client', variant: 'destructive' });
    }
  };

  const downloadDocument = async (docId, fileName) => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/clients/${id}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download document', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold">Client not found</h2>
        <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Client #{client.id}</h1>
        </div>
        <div className="flex gap-3">
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowApprovalTerms(true)} data-testid="approve-deal-btn">
            <CheckCircle size={16} className="mr-2" />
            Approve Deal
          </Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={handleDecline} data-testid="decline-deal-btn">
            <XCircle size={16} className="mr-2" />
            Decline Deal
          </Button>
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setShowOfferBuilder(true)} data-testid="create-offer-btn">
            <DollarSign size={16} className="mr-2" />
            Create Offer
          </Button>
          <Button variant="outline" onClick={() => setShowUploadDocs(true)} data-testid="upload-documents-btn">
            <Upload size={16} className="mr-2" />
            Upload Documents
          </Button>
          <Button variant="outline" onClick={() => setShowEditClient(true)} data-testid="edit-client-btn">
            <Edit size={16} className="mr-2" />
            Edit Client
          </Button>
        </div>
      </div>

      {/* General Information */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-xl font-bold mb-6">General Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
          <div>
            <p className="text-sm text-gray-500">First Name</p>
            <p className="text-gray-900" data-testid="client-first-name">{client.first_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Last Name</p>
            <p className="text-gray-900" data-testid="client-last-name">{client.last_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Contact Name</p>
            <p className="text-gray-900 font-semibold" data-testid="client-contact-name">
              {client.contact_name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Client Type</p>
            <p className="text-gray-900">{client.client_type || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Business Start Day</p>
            <p className="text-gray-900">{client.business_start_date ? new Date(client.business_start_date).toLocaleDateString() : 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Joined</p>
            <p className="text-gray-900">{client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Date of Birth</p>
            <p className="text-gray-900">{client.dob ? new Date(client.dob).toLocaleDateString() : 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">SSN</p>
            <p className="text-gray-900">{client.ssn || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Monthly Revenue</p>
            <p className="text-gray-900 font-semibold">{client.revenue || client.monthly_revenue || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">REP</p>
            <p className="text-gray-900">{client.rep || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">No. Of Owners</p>
            <p className="text-gray-900">{owners.length || 1}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Industry</p>
            <p className="text-gray-900">{client.industry || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Federal Tax ID</p>
            <p className="text-gray-900">{client.federal_tax_id || client.ein || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Business Name</p>
            <p className="text-gray-900 font-semibold">{client.business_name || client.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Expected Grade</p>
            <p className="text-gray-900">{client.expected_grade || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Doing Business As</p>
            <p className="text-gray-900">{client.dba || client.name || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Contact Information & Mailing Address */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-6">Contact Information</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Business Phone</p>
                <p className="text-gray-900">{client.phone || client.business_phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cell Phone</p>
                <p className="text-gray-900">{client.cell_phone || 'N/A'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Home Phone</p>
                <p className="text-gray-900">{client.home_phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fax</p>
                <p className="text-gray-900">{client.fax || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-gray-900">{client.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Website</p>
              <p className="text-gray-900">{client.website || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Street</p>
              <p className="text-gray-900">{client.address || client.street || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">City</p>
                <p className="text-gray-900">{client.city || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">State</p>
                <p className="text-gray-900">{client.state || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Zip Code</p>
              <p className="text-gray-900">{client.zip || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-6">Mailing Address</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Mailing Street</p>
              <p className="text-gray-900">{client.mailing_street || client.address || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Mailing City</p>
                <p className="text-gray-900">{client.mailing_city || client.city || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Mailing State</p>
                <p className="text-gray-900">{client.mailing_state || client.state || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mailing Zip Code</p>
              <p className="text-gray-900">{client.mailing_zip || client.zip || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Landlord / Mortgage Co.</p>
              <p className="text-gray-900">{client.landlord || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Rent / Mortgage Payment</p>
              <p className="text-gray-900">{client.rent_payment || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Bank Accounts</h3>
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowAddBankAccount(true)}>
            <Plus size={16} className="mr-2" />
            Add Bank Account
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4">Bank Account #</th>
              <th className="text-left py-2 px-4">Bank Account Name</th>
              <th className="text-left py-2 px-4">ACH Bank Routing #</th>
              <th className="text-left py-2 px-4">Wire Bank Routing #</th>
              <th className="text-left py-2 px-4">Bank Account Type</th>
              <th className="text-left py-2 px-4">Bank Name</th>
              <th className="text-left py-2 px-4">Is Main</th>
              <th className="text-left py-2 px-4">Edit</th>
              <th className="text-left py-2 px-4">Delete</th>
            </tr>
          </thead>
          <tbody>
            {bankAccounts.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-4 text-gray-500">No bank accounts</td></tr>
            ) : (
              bankAccounts.map((account, idx) => (
                <tr key={idx} className="border-b">
                  <td className="py-2 px-4">{account.account_number}</td>
                  <td className="py-2 px-4">{account.account_name}</td>
                  <td className="py-2 px-4">{account.ach_routing}</td>
                  <td className="py-2 px-4">{account.wire_routing}</td>
                  <td className="py-2 px-4">{account.type}</td>
                  <td className="py-2 px-4">{account.bank_name}</td>
                  <td className="py-2 px-4">{account.is_main ? '✓' : ''}</td>
                  <td className="py-2 px-4">
                    <button 
                      className="text-blue-600 hover:underline"
                      onClick={() => setEditingBankAccount(account)}
                    >
                      ✏️
                    </button>
                  </td>
                  <td className="py-2 px-4">
                    <button 
                      className="text-red-600 hover:underline"
                      onClick={() => deleteBankAccount(account.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Owner Information */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Owner Information</h3>
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowAddOwner(true)}>
            <Plus size={16} className="mr-2" />
            Add Owner
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4">#</th>
              <th className="text-left py-2 px-4">Name</th>
              <th className="text-left py-2 px-4">Ownership Date</th>
              <th className="text-left py-2 px-4">Title</th>
              <th className="text-left py-2 px-4">Ownership (%)</th>
              <th className="text-left py-2 px-4">Edit</th>
              <th className="text-left py-2 px-4">Delete</th>
            </tr>
          </thead>
          <tbody>
            {owners.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-4 text-gray-500">No owners</td></tr>
            ) : (
              owners.map((owner, idx) => (
                <tr key={idx} className="border-b">
                  <td className="py-2 px-4">{owner.id}</td>
                  <td className="py-2 px-4">{owner.name}</td>
                  <td className="py-2 px-4">{owner.ownership_date || 'n/a'}</td>
                  <td className="py-2 px-4">{owner.title}</td>
                  <td className="py-2 px-4">{owner.ownership_percent}%</td>
                  <td className="py-2 px-4">
                    <button 
                      className="text-blue-600 hover:underline"
                      onClick={() => setEditingOwner(owner)}
                    >
                      ✏️
                    </button>
                  </td>
                  <td className="py-2 px-4">
                    <button 
                      className="text-red-600 hover:underline"
                      onClick={() => deleteOwner(owner.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Documents</h3>
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowUploadDocs(true)}>
            <Upload size={16} className="mr-2" />
            Upload Documents
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4">Name</th>
              <th className="text-left py-2 px-4">Description</th>
              <th className="text-left py-2 px-4">Upload Date</th>
              <th className="text-left py-2 px-4">Delete</th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 ? (
              <tr><td colSpan="4" className="text-center py-4 text-gray-500">No documents</td></tr>
            ) : (
              documents.map((doc, idx) => (
                <tr key={idx} className="border-b">
                  <td 
                    className="py-2 px-4 text-blue-600 hover:underline cursor-pointer"
                    onClick={() => downloadDocument(doc.id, doc.name)}
                  >
                    📄 {doc.name}
                  </td>
                  <td className="py-2 px-4">{doc.description}</td>
                  <td className="py-2 px-4">{new Date(doc.upload_date).toLocaleDateString()}</td>
                  <td className="py-2 px-4">
                    <button 
                      className="text-red-600 hover:underline"
                      onClick={() => deleteDocument(doc.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showAddOwner && <AddOwnerModal clientId={id} onClose={() => { setShowAddOwner(false); loadClient(); }} />}
      {showAddBankAccount && <AddBankAccountModal clientId={id} onClose={() => { setShowAddBankAccount(false); loadClient(); }} />}
      {showUploadDocs && <UploadDocumentsModal clientId={id} onClose={() => setShowUploadDocs(false)} onSuccess={() => { setShowUploadDocs(false); loadDocuments(); }} />}
      {showEditClient && client && <EditClientModal client={client} onClose={() => setShowEditClient(false)} onSuccess={() => { setShowEditClient(false); loadClient(); }} />}
      {showOfferBuilder && client && <OfferBuilderModal clientId={id} clientName={client.name} onClose={() => setShowOfferBuilder(false)} onSuccess={() => { setShowOfferBuilder(false); navigate('/submitted-deals'); }} />}
      {showApprovalTerms && client && <ApprovalTermsModal isOpen={showApprovalTerms} onClose={() => setShowApprovalTerms(false)} client={client} onApprove={handleApproveWithTerms} />}
      {editingOwner && (
        <EditOwnerModal 
          clientId={id} 
          owner={editingOwner} 
          onClose={() => {
            setEditingOwner(null);
            loadClient();
          }} 
        />
      )}
      {editingBankAccount && (
        <EditBankAccountModal 
          clientId={id} 
          account={editingBankAccount} 
          onClose={() => {
            setEditingBankAccount(null);
            loadClient();
          }} 
        />
      )}
    </div>
  );
};

export default ClientDetail;